import { useState, useEffect, useRef, FormEvent } from 'react';
import { useRouter } from 'next/router';
import { isMobile, validateEmail, validatePassword } from '../../../utils/utils';
import type { TurnstileHandle } from '../../../components/Turnstile';

// Sólo permite redirigir a una ruta relativa propia (evita open-redirect si
// alguien arma un link a /registrarse?r=<base64 de una URL externa>).
const isSafeRedirect = (url: string) => url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');

type FeedbackType = 'error' | 'success';

const useHook = () => {

  const Router = useRouter();

  const [mobile, setMobile] = useState(true);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [password, setPassword] = useState("");
  const [feedbackPassword, setFeedbackPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [feedbackPassword2, setFeedbackPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('error');
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileHandle>(null);
  const handleTurnstileVerify = (token: string) => setTurnstileToken(token);
  const handleTurnstileExpire = () => setTurnstileToken("");

  const handleEmail = (val: string) => {
    const valido = validateEmail(val);
    setEmail(val.toLowerCase());
    setFeedbackEmail(valido ? "" : "Ingresá un email válido");
  };

  const handlePassword = (val: string) => {
    setPassword(val);
    const errores = validatePassword(val);
    setFeedbackPassword(errores.length == 0 ? "" : JSON.stringify(errores.join(" - ")));
  };

  const handlePassword2 = (val: string) => {
    setPassword2(val);
    setFeedbackPassword2(val == password ? "" : "Las contraseñas no coinciden");
  };

  const redirect = () => {
    let redirectTo = "";
    const { r } = Router.query;
    if (r) {
      const decoded = Buffer.from(r as string, 'base64').toString('ascii');
      redirectTo = isSafeRedirect(decoded) ? decoded : "";
    }
    setTimeout(() => {
      Router.push(redirectTo || "/ingresar");
    }, 1500);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const body = {
      username: email,
      email: email,
      password: password,
      turnstileToken,
    };

    try {
      const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/usuarios/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await result.json();
      if (data?.user?.id) {
        setEmail("");
        setPassword("");
        setPassword2("");
        setFeedbackType('success');
        setFeedbackMessage("¡Cuenta creada con éxito! Revisá tu casilla de email para confirmar tu cuenta.");
        redirect();
      } else {
        setFeedbackType('error');
        setFeedbackMessage(data.error?.message || 'Ocurrió un error inesperado');
      }
    } catch (err) {
      console.log(err);
      setFeedbackType('error');
      setFeedbackMessage('Ocurrió un error inesperado. Intentalo nuevamente.');
    } finally {
      setLoading(false);
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }
  };

  useEffect(() => {
    setMobile(isMobile());
  }, []);

  return {
    mobile,
    loading,
    handleSubmit,
    email,
    handleEmail,
    feedbackEmail,
    password,
    handlePassword,
    feedbackPassword,
    password2,
    handlePassword2,
    feedbackPassword2,
    showPassword,
    setShowPassword,
    feedbackType,
    feedbackMessage,
    turnstileRef,
    handleTurnstileVerify,
    handleTurnstileExpire,
    turnstileToken,
  };
};

export default useHook;
